import type {
  FilterDslOperator,
  FilterFieldType,
  FilterGroupNode,
  FilterLeafRule,
  FilterNode,
  FilterOperator,
  TaskFilters,
  TaskListSortBy,
} from '../types';

const PRIORITY_INT: Record<string, number> = {
  urgent: 4,
  high: 3,
  medium: 2,
  low: 1,
};

const SORT_FIELD_MAP: Partial<Record<string, string>> = {
  dueDate: 'dueDate',
  startDate: 'startDate',
  priority: 'priority',
  createdAt: 'createdAt',
  id: 'id',
};

type FieldSerializer = {
  buildRules: (
    values: string[],
    operator: FilterOperator,
    extra?: { from?: string; to?: string },
  ) => FilterNode[];
};

function buildSelectRules(field: string, values: string[], operator: FilterOperator): FilterNode[] {
  if (operator === 'isEmpty') return [{ field, operator: 'is_empty' }];
  if (operator === 'isNotEmpty') return [{ field, operator: 'is_not_empty' }];
  if (values.length === 0) return [];
  const dslOp: FilterDslOperator = operator === 'isNot' ? 'nin' : 'in';
  return [{ field, operator: dslOp, value: values }];
}

function buildUserRules(field: string, values: string[], operator: FilterOperator): FilterNode[] {
  return buildSelectRules(field, values, operator);
}

function buildDateRangeRules(
  field: string,
  from: string | undefined,
  to: string | undefined,
  operator: FilterOperator,
): FilterNode[] {
  if (operator === 'isEmpty') return [{ field, operator: 'is_empty' }];
  if (operator === 'isNotEmpty') return [{ field, operator: 'is_not_empty' }];
  const rules: FilterLeafRule[] = [];
  if (from) rules.push({ field, operator: 'gte', value: from });
  if (to) rules.push({ field, operator: 'lte', value: to });
  return rules;
}

function buildTitleRules(value: string | undefined, operator: FilterOperator): FilterNode[] {
  if (operator === 'isEmpty') return [{ field: 'title', operator: 'is_empty' }];
  if (operator === 'isNotEmpty') return [{ field: 'title', operator: 'is_not_empty' }];
  if (!value) return [];
  const dslOp: FilterDslOperator = operator === 'isNot' ? 'neq' : 'contains';
  return [{ field: 'title', operator: dslOp, value }];
}

const fieldSerializers: Record<FilterFieldType, FieldSerializer> = {
  status: {
    buildRules: (values, operator) => buildSelectRules('status', values, operator),
  },
  priority: {
    buildRules: (values, operator) => {
      if (operator === 'isEmpty') return [{ field: 'priority', operator: 'is_empty' }];
      if (operator === 'isNotEmpty') return [{ field: 'priority', operator: 'is_not_empty' }];
      if (values.length === 0) return [];
      const intValues = values.map((v) => PRIORITY_INT[v] ?? 0);
      const dslOp: FilterDslOperator = operator === 'isNot' ? 'nin' : 'in';
      return [{ field: 'priority', operator: dslOp, value: intValues }];
    },
  },
  assignee: {
    buildRules: (values, operator) => buildUserRules('assignee', values, operator),
  },
  tag: {
    buildRules: (values, operator) => buildSelectRules('tag', values, operator),
  },
  group: {
    buildRules: (values, operator) => buildSelectRules('group', values, operator),
  },
  board: {
    buildRules: (values, operator) => buildSelectRules('board', values, operator),
  },
  title: {
    buildRules: (values, operator) => buildTitleRules(values[0], operator),
  },
  dueDate: {
    buildRules: (_, operator, extra) =>
      buildDateRangeRules('dueDate', extra?.from, extra?.to, operator),
  },
  startDate: {
    buildRules: (_, operator, extra) =>
      buildDateRangeRules('startDate', extra?.from, extra?.to, operator),
  },
};

function getOperatorForField(field: FilterFieldType, filters: TaskFilters): FilterOperator {
  return filters.operatorsByField?.[field] ?? 'is';
}

/**
 * Converts UI filter state into the JSON DSL string expected by the tasks API.
 *
 * @param filters - UI filter state from `TaskFilters`
 * @returns Serialized JSON string, or `undefined` if no active filter rules exist.
 *   Returns `undefined` (not an empty string) so callers can safely spread it:
 *   `...(result && { filters: result })`.
 *
 * @remarks
 * - `priority` values are mapped to backend integers: urgent→4, high→3, medium→2, low→1.
 *   The API rejects string priority values.
 * - `title` uses `contains`/`neq` (text match), while all other select fields use `in`/`nin`.
 * - `isEmpty`/`isNotEmpty` operators emit rules without a `value` field (no `is` value needed).
 * - `filterLogic` controls the top-level `AND`/`OR` group; defaults to `AND`.
 */
export function serializeFilters(filters: TaskFilters): string | undefined {
  const logic = (filters.filterLogic ?? 'and').toUpperCase() as 'AND' | 'OR';
  const rules: FilterNode[] = [];

  const statusOp = getOperatorForField('status', filters);
  if (filters.statuses.length > 0 || statusOp === 'isEmpty' || statusOp === 'isNotEmpty') {
    rules.push(...fieldSerializers.status.buildRules(filters.statuses, statusOp));
  }

  const priorityOp = getOperatorForField('priority', filters);
  const priorityVals = (filters.priorities ?? []) as string[];
  if (priorityVals.length > 0 || priorityOp === 'isEmpty' || priorityOp === 'isNotEmpty') {
    rules.push(...fieldSerializers.priority.buildRules(priorityVals, priorityOp));
  }

  const assigneeOp = getOperatorForField('assignee', filters);
  if (filters.assigneeIds.length > 0 || assigneeOp === 'isEmpty' || assigneeOp === 'isNotEmpty') {
    rules.push(...fieldSerializers.assignee.buildRules(filters.assigneeIds, assigneeOp));
  }

  const tagOp = getOperatorForField('tag', filters);
  const tagVals = filters.tagIds ?? [];
  if (tagVals.length > 0 || tagOp === 'isEmpty' || tagOp === 'isNotEmpty') {
    rules.push(...fieldSerializers.tag.buildRules(tagVals, tagOp));
  }

  const groupOp = getOperatorForField('group', filters);
  const groupVals = filters.groupIds ?? [];
  if (groupVals.length > 0 || groupOp === 'isEmpty' || groupOp === 'isNotEmpty') {
    rules.push(...fieldSerializers.group.buildRules(groupVals, groupOp));
  }

  const boardOp = getOperatorForField('board', filters);
  const boardVals = filters.boardIds ?? [];
  if (boardVals.length > 0 || boardOp === 'isEmpty' || boardOp === 'isNotEmpty') {
    rules.push(...fieldSerializers.board.buildRules(boardVals, boardOp));
  }

  const titleOp = getOperatorForField('title', filters);
  if (filters.titleValue || titleOp === 'isEmpty' || titleOp === 'isNotEmpty') {
    rules.push(
      ...fieldSerializers.title.buildRules(filters.titleValue ? [filters.titleValue] : [], titleOp),
    );
  }

  const dueDateOp = getOperatorForField('dueDate', filters);
  if (
    filters.dueDateFrom ||
    filters.dueDateTo ||
    dueDateOp === 'isEmpty' ||
    dueDateOp === 'isNotEmpty'
  ) {
    rules.push(
      ...fieldSerializers.dueDate.buildRules([], dueDateOp, {
        from: filters.dueDateFrom,
        to: filters.dueDateTo,
      }),
    );
  }

  const startDateOp = getOperatorForField('startDate', filters);
  if (
    filters.startDateFrom ||
    filters.startDateTo ||
    startDateOp === 'isEmpty' ||
    startDateOp === 'isNotEmpty'
  ) {
    rules.push(
      ...fieldSerializers.startDate.buildRules([], startDateOp, {
        from: filters.startDateFrom,
        to: filters.startDateTo,
      }),
    );
  }

  if (rules.length === 0) return undefined;

  const group: FilterGroupNode = { operator: logic, rules };
  return JSON.stringify(group);
}

/**
 * Converts a UI sort selection into the JSON sort array expected by the tasks API.
 *
 * @param sortBy - Column and direction selected in the UI
 * @returns Serialized JSON string: `[{ field, direction }]`
 */
export function serializeSort(sortBy: TaskListSortBy): string {
  const field = SORT_FIELD_MAP[sortBy.columnId] ?? sortBy.columnId;
  return JSON.stringify([{ field, direction: sortBy.direction }]);
}
